import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'

const SIZE = 22
const STROKE = 2
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

type Props = {
  // How long until the card auto-dismisses — the ring drains over exactly
  // this duration so it reads as a literal countdown, not a decoration.
  durationMs: number
  color: string
  trackColor: string
  onClick: () => void
  ariaLabel: string
}

// A close (X) button with a circular countdown ring drawn around it, timed to
// match the card's own auto-dismiss timeout. Purely presentational — the
// timer that actually hides the card lives in the card itself; this just
// visualizes it.
export const AutoDismissCloseButton: React.FC<Props> = ({ durationMs, color, trackColor, onClick, ariaLabel }) => {
  const [drained, setDrained] = useState(false)

  useEffect(() => {
    // One frame so the browser paints the full (offset 0) ring before the
    // transition to "drained" starts — without this the ring would just
    // render already-empty instead of visibly counting down.
    const raf = requestAnimationFrame(() => setDrained(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        width: SIZE,
        height: SIZE,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width={SIZE} height={SIZE} style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" stroke={trackColor} strokeWidth={STROKE} />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={drained ? CIRCUMFERENCE : 0}
          style={{ transition: `stroke-dashoffset ${durationMs}ms linear` }}
        />
      </svg>
      <X size={12} style={{ color, position: 'relative' }} />
    </button>
  )
}
